import { useMemo } from 'react';
import {
	ConvertedImageRef,
	buildConvertedImageIndex,
	findConvertedImageRef,
} from '../utils/ConvertedImageUtils';
import { isHeicUrl, useHeicSafeSrc } from './useHeicSafeSrc';
import { getPendingOriginalFromUrl, useConvertedImageStatus } from './useConvertedImageStatus';
import { PendingOriginal } from './convertedImageStatusStore';

/**
 * The single decision behind rendering any stored image, so no caller has to know that HEIC
 * exists.
 *
 * Four sources answer "what URL do I render", in priority order, and the point of this hook is
 * that they are tried in the same order everywhere:
 *
 *   1. the entity's own `convertedImages` — free, already on the response
 *   2. the conversion-status poll — the only source on a CREATE form, where no entity exists yet
 *      to carry `convertedImages`
 *   3. the in-browser heic2any decode — the fallback once the backend says it has nothing coming
 *   4. the original URL — for everything that is not a HEIC, which is almost everything
 *
 * `isConverting` is true whenever there is no renderable URL yet. Callers render a placeholder on
 * it; rendering `<img src="">` instead is what produced a broken-image icon.
 */
export interface ResolvedImageSrc {
	/** The thumbnail-sized URL to render, or '' while nothing is renderable yet. */
	src: string;
	/** Full-size URL for a lightbox — the derivative, not the 11KB thumbnail. */
	previewSrc: string;
	isConverting: boolean;
}

// A stored image is identified by either half depending on which column it came from, so both are
// offered and whichever the conversion row carries will match.
export const toPendingOriginal = (
	fileId?: string | null,
	s3ObjectKey?: string | null
): PendingOriginal | null => {
	if (!fileId && !s3ObjectKey) {
		return null;
	}
	return { fileId: fileId || undefined, s3ObjectKey: s3ObjectKey || undefined };
};

export interface ResolvedImageInput {
	/** The URL to render when nothing needs converting. */
	url: string;
	/** Omit both and they are derived from `url`, which is what note/chat photos carry. */
	fileId?: string | null;
	/** For the private bucket this is the `fileId/fileName` string. */
	s3ObjectKey?: string | null;
	/** The entity's sibling list, when the caller has an entity at all. */
	convertedImages?: ConvertedImageRef[] | null;
}

export const useResolvedImageSrc = ({
	url,
	fileId,
	s3ObjectKey,
	convertedImages,
}: ResolvedImageInput): ResolvedImageSrc => {
	// Notes and chat store an absolute URL and nothing else, so the identity is recovered from it
	// rather than made a burden on every caller.
	const identity = useMemo(() => {
		if (fileId || s3ObjectKey) {
			return { fileId: fileId || undefined, s3ObjectKey: s3ObjectKey || undefined };
		}
		return getPendingOriginalFromUrl(url) || {};
	}, [fileId, s3ObjectKey, url]);

	const entityRef = useMemo(
		() => findConvertedImageRef(buildConvertedImageIndex(convertedImages), identity),
		[convertedImages, identity]
	);

	// Poll only for a HEIC the entity could not already resolve. An empty list makes the hook
	// inert — no request, no timer — which is the case for the overwhelming majority of images.
	const pendingOriginals = useMemo(() => {
		if (entityRef || !isHeicUrl(url)) {
			return [];
		}
		const original = toPendingOriginal(identity.fileId, identity.s3ObjectKey);
		return original ? [original] : [];
	}, [entityRef, url, identity]);

	const convertedImageStatus = useConvertedImageStatus(pendingOriginals);
	const pendingOriginal = pendingOriginals.length ? pendingOriginals[0] : null;
	const polledUrl = convertedImageStatus.getConvertedUrl(pendingOriginal);
	const isPolling = convertedImageStatus.isConverting(pendingOriginal);

	// Hold the main-thread decode off while the backend still owes us a derivative: its JPEG is
	// better than ours and costs this tab nothing. Passing '' makes the hook idle.
	const shouldDecodeLocally = !entityRef && !polledUrl && !isPolling;
	const heicSafeSrc = useHeicSafeSrc(shouldDecodeLocally ? url : '');

	if (entityRef) {
		return {
			src: entityRef.convertedThumbUrl || (entityRef.convertedUrl as string),
			previewSrc: entityRef.convertedUrl as string,
			isConverting: false,
		};
	}
	if (polledUrl) {
		return { src: polledUrl, previewSrc: polledUrl, isConverting: false };
	}
	if (isPolling) {
		return { src: '', previewSrc: '', isConverting: true };
	}
	if (!isHeicUrl(url)) {
		return { src: url, previewSrc: url, isConverting: false };
	}
	return {
		src: heicSafeSrc.src,
		previewSrc: heicSafeSrc.src,
		isConverting: heicSafeSrc.isConverting,
	};
};
